import { cx } from "@/lib/cx";
import type { DecodeResult } from "@/modules/plus/decoder/plus.decoder";
import { PhotoInline } from "@/modules/plus/photos/components/PhotoInline";

const EmptyState: React.FC = () => (
  <div className="rounded-xl border border-dashed border-gray-300 bg-white/60 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
    Start typing to see the breakdown.
  </div>
);

const NoModelState: React.FC<{
  result: Extract<DecodeResult, { error: "no_model" }>;
}> = ({ result }) => (
  <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm dark:border-amber-900 dark:bg-amber-950/50">
    <div className="font-semibold text-amber-900 dark:text-amber-200">No model matches</div>
    <p className="mt-1 text-amber-800 dark:text-amber-300">
      We couldn't match <code className="font-mono">{result.firstToken}</code> to a known model
      prefix.
    </p>
    {result.suggestions.length > 0 && (
      <div className="mt-3">
        <div className="text-xs font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400">
          Closest model codes
        </div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {result.suggestions.map((s) => (
            <code
              key={s}
              className="rounded bg-amber-100 px-2 py-0.5 font-mono text-xs text-amber-900 dark:bg-amber-900 dark:text-amber-100"
            >
              {s}
            </code>
          ))}
        </div>
      </div>
    )}
    {result.candidates.length > 0 && (
      <div className="mt-3">
        <div className="text-xs font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400">
          Maybe you meant a model with this option?
        </div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {result.candidates.map((c) => (
            <code
              key={c}
              className="rounded bg-amber-100 px-2 py-0.5 font-mono text-xs text-amber-900 dark:bg-amber-900 dark:text-amber-100"
            >
              {c}
            </code>
          ))}
        </div>
      </div>
    )}
  </div>
);

const DbOnlyState: React.FC<{
  result: Extract<DecodeResult, { kind: "dbOnly" }>;
}> = ({ result }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
    <div className="flex items-baseline justify-between gap-3">
      <div>
        <div className="font-mono text-lg font-semibold dark:text-gray-100">{result.model}</div>
        <div className="text-sm text-gray-600 dark:text-gray-400">{result.db.description}</div>
      </div>
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Database only
      </div>
    </div>
    {result.db.photo_url && (
      <img
        src={result.db.photo_url}
        alt={result.db.description}
        className="mt-3 h-32 w-32 rounded-lg border border-gray-200 object-cover"
      />
    )}
    <p className="mt-3 text-xs text-gray-600">{result.warnings[0]}</p>
    {result.leftover.length > 0 && (
      <div className="mt-3 text-xs text-gray-500">
        Unparsed segments: <code className="font-mono">{result.leftover.join("-")}</code>
      </div>
    )}
  </div>
);

const FullDecodeState: React.FC<{
  result: Extract<DecodeResult, { kind: "full" }>;
}> = ({ result }) => {
  const photo = result.db?.photo_url ?? result.dbFallback?.db.photo_url;
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-lg font-semibold dark:text-gray-100">{result.model}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">{result.modelMeaning}</div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{result.heading}</div>
            {result.page && (
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Catalog page: {result.page}
              </div>
            )}
          </div>
          {photo && (
            <img
              src={photo}
              alt={result.modelMeaning}
              className="h-24 w-24 rounded-lg border border-gray-200 object-cover"
            />
          )}
        </div>
        {result.dbFallback && (
          <div className="mt-2 text-xs text-gray-500">
            (Photo from sibling model <code className="font-mono">{result.dbFallback.from}</code>)
          </div>
        )}
      </div>

      {result.fields.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-400">
                <th className="px-4 py-2 font-medium">Field</th>
                <th className="px-4 py-2 font-medium">Token</th>
                <th className="px-4 py-2 font-medium">Meaning</th>
                <th className="px-4 py-2 font-medium">Photo</th>
              </tr>
            </thead>
            <tbody>
              {result.fields.map((f, i) => (
                <tr
                  // biome-ignore lint/suspicious/noArrayIndexKey: field names can repeat; rows are positional
                  key={`${f.name}-${i}`}
                  className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                >
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{f.name}</td>
                  <td className="px-4 py-2 font-mono dark:text-gray-200">{f.token}</td>
                  <td
                    className={cx(
                      "px-4 py-2",
                      f.warning
                        ? "text-amber-700 dark:text-amber-400"
                        : "text-gray-900 dark:text-gray-100",
                      f.isDefault ? "italic" : undefined,
                    )}
                  >
                    {f.meaning}
                    {f.warning && (
                      <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                        ⚠ {f.warning}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <PhotoInline code={f.token} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result.accessories.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Accessories
          </div>
          <ul className="mt-2 space-y-1.5">
            {result.accessories.map((a) => (
              <li key={a.code} className="text-sm">
                <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-gray-800 dark:text-gray-200">
                  {a.code}
                </code>{" "}
                <span className="text-gray-800 dark:text-gray-200">{a.meaning}</span>{" "}
                <span className="text-xs text-gray-500 dark:text-gray-400">— {a.category}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.warnings.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
          <div className="font-medium">Warnings</div>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-amber-800 dark:text-amber-300">
            {result.warnings.map((w, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: warnings are plain strings that may repeat
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const DecoderResult: React.FC<{ result: DecodeResult }> = ({ result }) => {
  if (!result.ok) {
    if (result.error === "empty") {
      return <EmptyState />;
    }
    return <NoModelState result={result} />;
  }
  if (result.kind === "dbOnly") {
    return <DbOnlyState result={result} />;
  }
  return <FullDecodeState result={result} />;
};
